import { prepareCardForSave } from '@modules/card-helpers';
import { collections, firestore, functions } from '@modules/firebase';
import { logger } from '@modules/logger';
import { sanitizeForDb, toCard, toDBCard } from '@modules/transformers';
import { assertIsType, assertIsTypedArray } from '@modules/types';
import { doc, getDoc, getDocs, onSnapshot, query, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { AnonymousUser, Card, User } from 'types';

import { isDBCard } from './predicates/card-predicates';

/**
 * Save a cards state. This will create a new card if the card doesn't already exist
 * in the DB, otherwise it will overwrite the existing card. Merge has to be true in order to maintain
 * any card state created that the frontend is not aware of (e.g. state created by a cloud function)
 * @param card The state of the card
 */
export async function saveCard(card: Card, user: User | AnonymousUser): Promise<void> {
  const cardToPersist = prepareCardForSave(card);

  const batch = writeBatch(firestore);

  const cardDocRef = doc(collections.cards(), cardToPersist.cardId);
  const cardEditsDocRef = doc(collections.cardEdits(), cardToPersist.cardEditId);

  const dbCard = toDBCard(cardToPersist);
  const sanitizedDbCard = sanitizeForDb(dbCard);

  batch.set(cardDocRef, sanitizedDbCard, { merge: true });
  batch.set(cardEditsDocRef, sanitizedDbCard, { merge: true });

  // If this is your card (and not a team members card) then update your card IDs
  if (card.userId === user.userId) {
    const userDocRef = doc(collections.users(), user.userId);

    const updatedCardIds = [...new Set([...user.cardIds, card.cardId])];
    batch.set(userDocRef, { card_ids: updatedCardIds }, { merge: true });
  }

  return batch.commit();
}

/**
 * Gets a single card by ID
 * @param id ID of the card
 */
export async function getCard(id: string): Promise<Card> {
  const snapshot = await getDoc(doc(collections.cards(), id));
  const card = snapshot.data();
  assertIsType(card, isDBCard);

  return toCard(card);
}

/**
 * Fetch all the cards that are owned by a given user
 * @param userId The ID of the user whose cards we are fetching
 */
export async function getCardsByUser(userId: string): Promise<Card[]> {
  const docsQuery = query(collections.cards(), where('user_id', '==', userId));

  const snapshot = await getDocs(docsQuery);
  const cards = snapshot.docs.map((doc) => doc.data());
  assertIsTypedArray(cards, isDBCard);

  // TODO: find out and document why we don't filter while querying Firestore
  return cards.map((card) => toCard(card)).filter(({ isInvisible }) => !isInvisible);
}

/**
 * Subscribe to changes to any card that is owned by a given user
 * @param userId The ID of the user whose cards we are fetching
 */
export function subscribeToCardsByUser(
  userId: string,
  onUpdate: (cards: Card[]) => void,
): () => void {
  const docsQuery = query(collections.cards(), where('user_id', '==', userId));

  // https://github.com/firebase/firebase-js-sdk/issues/3053#issuecomment-628193783
  const unsubscribe = onSnapshot(docsQuery, { includeMetadataChanges: true }, (snapshot) => {
    // The cache is unreliable because depending on the order the queries run this can return partial results.
    // This happens when a query for an individual card hits the DB before this query runs, which means the cache
    // is now primed with a single result.
    if (snapshot.metadata.fromCache) return;

    const cards = snapshot.docs.map((doc) => doc.data());
    assertIsTypedArray(cards, isDBCard);

    // TODO: find out and document why we don't filter while querying Firestore
    onUpdate(cards.map((card) => toCard(card)).filter(({ isInvisible }) => !isInvisible));
  });

  return unsubscribe;
}

/**
 * Get all the cards in a given organisation. This will soon have to be reimplemented to give
 * all cards in a workspace instead.
 * @param orgId The organisation ID
 */
export async function getAllWorkspaceCards(orgId: string): Promise<Card[]> {
  const docsQuery = query(collections.cards(), where('org_id', '==', orgId));

  const snapshot = await getDocs(docsQuery);
  const cards = snapshot.docs.map((doc) => doc.data());
  assertIsTypedArray(cards, isDBCard);

  // TODO: find out and document why we don't filter while querying Firestore
  return cards.map((card) => toCard(card)).filter(({ isInvisible }) => !isInvisible);
}

/**
 * Subscribe to changes to any card is part of a specific workspace
 * @param orgId The organisation ID
 */
export function subscribeToAllWorkspaceCards(
  orgId: string,
  onUpdate: (cards: Card[]) => void,
): () => void {
  const docsQuery = query(collections.cards(), where('org_id', '==', orgId));

  const unsubscribe = onSnapshot(docsQuery, (snapshot) => {
    const cards = snapshot.docs.map((doc) => doc.data());
    assertIsTypedArray(cards, isDBCard);

    // TODO: find out and document why we don't filter while querying Firestore
    onUpdate(cards.map((card) => toCard(card)).filter(({ isInvisible }) => !isInvisible));
  });

  return unsubscribe;
}

export const generateEmailSignature = async (cardId: string): Promise<string> => {
  logger.info('Calling firebase function', { function: 'renderEmailSignature' });
  const generateEmailSignature = httpsCallable<{ cardId: string }>(
    functions,
    'renderEmailSignature',
  );

  const result = await generateEmailSignature({ cardId });

  // TODO: where's error handling?
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error The Firebase function can return anything
  return result.data?.signatureId as string;
};
